import { HeroDustField } from './HeroDustField';
import { HeaderNavV2 } from './HeaderNavV2';
import { cn } from '~/lib/utils';
import { FeaturesV2 } from './FeaturesV2';
import { OneWorkspaceV2 } from './OneWorkspaceV2';
import { HeroSectionV2 } from './HeroSectionV2';
import { TrustedByLogos } from '~/landing_page/components/TrustedByLogos/TrustedByLogos';
import { WhoItsForSection } from '~/landing_page/components/WhoItsForSection';
import { PricingSection } from '~/landing_page/components/PricingSection/PricingSection';
import { PrivacySection } from '~/landing_page/components/PrivacySection/PrivacySection';
import { FAQSection } from '~/landing_page/components/FAQSection';
import { CTASection } from '~/landing_page/components/CTASection/CTASection';
import { LogoSection } from '~/landing_page/components/LogoSection/LogoSection';
import { FooterSection } from '~/landing_page/components/FooterSection/FooterSection';
import { CookieBanner } from '~/landing_page/components/CookieBanner/CookieBanner';
import styles from './LandingPageV2.module.css';

/**
 * The dark, paper.design-shaped landing: the public face of `/`, built one section at a time from the
 * Paper "Landing page" file. The v2-designed sections come first (hero, strip, features, globe); the
 * rest of the light landing follows, turned dark by the token remap on the root, in the same order
 * the current `LandingPage` stacks them.
 */
export interface LandingPageV2Props {
  className?: string;
}

export function LandingPageV2({ className }: LandingPageV2Props) {
  return (
    <div className={cn(styles.root, className)} data-name="LandingPageV2">
      {/* The dashboard's dust, loosed over the whole hero: the same grain and palette as the app's
          sky, with roaming sites instead of two fixed ones. Behind the nav and the hero alike. */}
      <HeroDustField />
      {/* The same field at the foot, so the page closes the way it opens. Early in the tree on
          purpose: it is decoration, and every later sibling paints over it without needing a
          z-index — which is exactly how the top one already works. */}
      <HeroDustField
        className={styles.dustFieldFoot}
        flipped
        wordSrc="/landing/logo-visual/logo-visual.svg"
        wordAnchor="[data-name='LogoSection'] img"
      />
      <HeaderNavV2 />
      <HeroSectionV2 />
      {/* The light landing's strip, as is: one line, moving, fading at both edges (Guri, 2026-09-04). */}
      <TrustedByLogos tone="dark" />
      <FeaturesV2 id="features" />
      {/* The workspace bento from Paper "07 One workspace · dark": artifacts as live code, the
          model picker, the brand kit, the tools, the share row (Guri, 2026-09-04). */}
      <OneWorkspaceV2 id="workspace" />
      {/* The light landing's globe of decks, as is, in its dark tone. */}
      <WhoItsForSection id="use-cases" tone="dark" />
      {/* The rest of the light landing, as is. They read the colour tokens the root remaps, so
          they arrive dark without a rebuild; pricing and the footer carry the two hex swaps. */}
      <PricingSection id="pricing" tone="dark" />
      {/* No photograph on v2 at all (Beka, 2026-09-12): the card is the page's own dust and nothing
          else, so the section reads as this page rather than as a picture dropped into it. The light
          landing keeps its original plate, and with it the gradient that photograph needs. */}
      <PrivacySection
        id="privacy"
        backgroundSrc={null}
        backdrop={<HeroDustField className={styles.dustFieldPrivacy} />}
        dim={false}
      />
      <FAQSection id="faq" />
      <CTASection tone="dark" />
      <LogoSection tone="dark" showWordmark={false} />
      <FooterSection tone="dark" />
      <CookieBanner theme="dark" />
    </div>
  );
}

LandingPageV2.displayName = 'LandingPageV2';
